<script lang="ts">
	import { format } from 'svelte-i18n';
	import { createEventDispatcher } from 'svelte';

	import { Alert, Button, Container, Modal } from '@Shelly/shelly-design-system';

	import PasswordUtility from 'src/components/PasswordUtility.svelte';
	import SignUpConsentCheckboxes from 'src/components/Profiles/signup/SignUpConsentCheckboxes.svelte';
	import { DEFAULT_SIGNUP_PASSWORD_INPUT_PROPS } from 'src/helpers/signup_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import type { InputProperties } from 'src/types/InputProperties';

	export let showModal = false;
	export let loading = false;
	/** When true, collect a password here (decline invite has no password field on the page). */
	export let collectPassword = false;
	export let alertTitleKey = 'unit_transfer_create_account_title';
	export let alertTitleDefault = 'Home transfer is no longer available';
	export let alertMessageKey = 'unit_transfer_create_account_message';
	export let alertMessageDefault =
		'This home transfer was cancelled or has expired. You can still create your Shelly account now. Later, ask the owner to send a new transfer invitation.';

	export let passwordInputProps: InputProperties = { ...DEFAULT_SIGNUP_PASSWORD_INPUT_PROPS };
	export let passwordError = '';

	const dispatch = createEventDispatcher<{ confirm: void; close: void }>();

	let termsCheck = false;
	let privacyPolicyCheck = false;
	let newsletterCheck = false;

	$: if (!showModal) {
		termsCheck = false;
		privacyPolicyCheck = false;
		newsletterCheck = false;
		if (collectPassword) {
			passwordInputProps = { ...DEFAULT_SIGNUP_PASSWORD_INPUT_PROPS };
			passwordError = '';
		}
	}

	$: hasPassword = Boolean(passwordInputProps.value?.length);
	$: canCreateAccount = termsCheck && privacyPolicyCheck && (!collectPassword || hasPassword);

	function onClose(): void {
		if (loading) return;
		showModal = false;
		dispatch('close');
	}

	function onConfirm(): void {
		if (loading || !canCreateAccount) return;
		dispatch('confirm');
	}

	function clearPasswordError(): void {
		passwordError = '';
	}
</script>

<Modal
	{showModal}
	on:close={onClose}
	title={capFirstChar($format('button_sign_up', { default: 'Sign up' }))}
	removeScrollPadding
>
	<svelte:fragment slot="content">
		<Container padding="scrollPaddings">
			<Alert
				type="warning"
				class="mb-m"
				isHeadingBold
				heading={$format(alertTitleKey, { default: alertTitleDefault })}
				message={$format(alertMessageKey, { default: alertMessageDefault })}
			/>

			{#if collectPassword}
				<PasswordUtility
					bind:inputProps={passwordInputProps}
					bind:error={passwordError}
					on:focus={clearPasswordError}
				/>
			{/if}

			<SignUpConsentCheckboxes
				idPrefix="unit-transfer-create-account"
				bind:termsCheck
				bind:privacyPolicyCheck
				bind:newsletterCheck
				disabled={loading}
				showDataAct={true}
			/>

			<Button
				class="text-capitalize mb-s"
				variant="primary"
				width="full"
				disabled={!canCreateAccount || loading}
				{loading}
				aria-label={$format('button_unit_transfer_create_account', {
					default: 'Create account',
				})}
				on:click={onConfirm}
			>
				{capFirstChar(
					$format('button_unit_transfer_create_account', {
						default: 'Create account',
					}),
				)}
			</Button>

			<Button
				variant="secondary"
				width="full"
				disabled={loading}
				aria-label={$format('button_cancel', { default: 'Cancel' })}
				on:click={onClose}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
		</Container>
	</svelte:fragment>
</Modal>
